import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { verifyAuthWebAuthVerifyPost } from "../../__generated__/api/web/web";
import type { AuthVerifyResponse } from "../../__generated__/api/wrenAPI.schemas";
import type { CustomRequestInit } from "../../api/client";

export function AuthPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setToken, logout } = useAuth();
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get("token");
  const isLogout = searchParams.has("logout");
  const isExpired = searchParams.has("expired");

  useEffect(() => {
    // Handle logout
    if (isLogout) {
      logout();
      navigate("/", { replace: true });
      return;
    }

    // Handle token verification
    if (token) {
      setIsVerifying(true);
      setError(null);

      // Use skip401Redirect to avoid the global 401 handler.
      // A 401 here means the magic link is invalid/expired — it should NOT clear
      // an existing valid session token.
      verifyAuthWebAuthVerifyPost(
        { token },
        { skip401Redirect: true } as CustomRequestInit
      )
        .then((response) => {
          const data = response.data as AuthVerifyResponse;
          setToken(data.token);
          navigate("/", { replace: true });
        })
        .catch(() => {
          setError("Invalid or expired link");
        })
        .finally(() => {
          setIsVerifying(false);
        });
      return;
    }

    // Handle expired session (show message, don't redirect)
    if (isExpired) {
      setError("Your session has expired. Please request a new link.");
      return;
    }

    // No params - redirect to home
    navigate("/", { replace: true });
  }, [token, isLogout, isExpired, logout, setToken, navigate]);

  // Show loading while verifying
  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-text-secondary">Verifying...</div>
      </div>
    );
  }

  // Show error message
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-semibold text-text-primary mb-4">
            {isExpired ? "Session Expired" : "Link Expired"}
          </h1>
          <p className="text-text-secondary mb-6">
            To get a new link, text Wren:
          </p>
          <p className="text-text-primary font-medium">
            "Hey, Wren! Can you send me a magic link to login?"
          </p>
        </div>
      </div>
    );
  }

  // Default: show nothing while redirecting
  return null;
}
